/**
 * Purchase invoices (Phase 2 W5-2; docs/plans/PHASE_2_W5.md §4). Shared by the API, the admin web
 * and (from W5-4) Dob Desktop, so every screen and the server compute the same numbers:
 *
 * - a line's total = round_half_up(quantity in stock units × price ÷ the price's unit factor);
 * - an invoice discount is spread over the lines by their totals, largest remainder, so the net
 *   lines add up to the invoice exactly;
 * - the same supplier and invoice number twice is refused, the number compared without spaces or
 *   case, Arabic-Indic digits read as Latin;
 * - a price more than `purchase.price_jump_alert_bp` above the last price of the item, per stock
 *   unit, needs a written reason (exactly at the limit needs none).
 *
 * The database checks the same rules again (0029); these give the reasons in words before saving.
 */
import { z } from 'zod';

import { PhotoHandoffRefSchema } from '../attachments/handoff.ts';
import { isUuidV7 } from '../ids/ids.ts';
import { parseQuantity, type QuantityProblem, type StockUnit } from '../inventory/quantities.ts';
import { allocate, divideRounded, iqd } from '../money/money.ts';
import { latinDigits, stripInvisible } from '../network/address.ts';

const SAFE = Number.MAX_SAFE_INTEGER;

export const PAYMENT_KINDS = ['CREDIT', 'CASH'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

export const PRICE_JUMP_SETTING = 'purchase.price_jump_alert_bp';
export const INVOICE_LINES_MAX = 100;
export const INVOICE_PHOTOS_MAX = 10;
export const INVOICE_NUMBER_MAX = 40;
export const PRICE_JUMP_REASON_MIN = 5;
export const CANCEL_REASON_MIN = 10;

/**
 * The invoice number as compared for "the same number twice": spaces and invisible marks removed,
 * Arabic-Indic digits as Latin, upper case. Dashes and slashes count («INV-12» ≠ «INV12»).
 */
export function normalizeInvoiceNumber(text: string): string {
  return latinDigits(stripInvisible(text)).replace(/\s+/g, '').toUpperCase();
}

/** A line's total in dinars: round_half_up(quantity × price ÷ factor), exact in BigInt. */
export function invoiceLineTotal(quantity: number, unitPrice: number, unitFactor: number): number {
  if (!Number.isSafeInteger(quantity) || quantity <= 0) throw new RangeError('quantity');
  if (!Number.isSafeInteger(unitPrice) || unitPrice < 0) throw new RangeError('unitPrice');
  if (!Number.isSafeInteger(unitFactor) || unitFactor < 1) throw new RangeError('unitFactor');
  const total = divideRounded(BigInt(quantity) * BigInt(unitPrice), BigInt(unitFactor), 'HALF_UP');
  if (total > BigInt(SAFE)) throw new RangeError('total too large');
  return Number(total);
}

/**
 * The discount spread over the lines in proportion to their totals (largest remainder), so the net
 * lines always add up to (sum of lines − discount). Null when the discount is more than the lines
 * (or there is a discount on lines worth nothing).
 */
export function spreadDiscount(lineTotals: readonly number[], discount: number): number[] | null {
  if (!Number.isSafeInteger(discount) || discount < 0) return null;
  const gross = lineTotals.reduce((a, b) => a + b, 0);
  if (discount > gross) return null;
  if (discount === 0) return [...lineTotals];
  const shares = allocate(iqd(discount), lineTotals, 'LARGEST_REMAINDER').shares;
  return lineTotals.map((total, i) => total - (shares[i] ?? 0));
}

/** The last price of an item, per `unitFactor` stock units (from its most recent invoice line). */
export interface LastPrice {
  readonly unitPrice: number;
  readonly unitFactor: number;
}

/**
 * Whether `unitPrice` per `unitFactor` stock units is more than `jumpBp` basis points above the last
 * price, compared per stock unit with integers only:
 * price × lastFactor × 10,000 > lastPrice × factor × (10,000 + bp). No last price → never a jump.
 */
export function priceJumped(
  price: { readonly unitPrice: number; readonly unitFactor: number },
  last: LastPrice | null,
  jumpBp: number,
): boolean {
  if (last === null) return false;
  const left = BigInt(price.unitPrice) * BigInt(last.unitFactor) * 10_000n;
  const right = BigInt(last.unitPrice) * BigInt(price.unitFactor) * BigInt(10_000 + jumpBp);
  return left > right;
}

/** How much above the last price, in basis points per stock unit (for the words on the screen). */
export function priceRiseBp(
  price: { readonly unitPrice: number; readonly unitFactor: number },
  last: LastPrice,
): number | null {
  if (last.unitPrice === 0) return null;
  const now = BigInt(price.unitPrice) * BigInt(last.unitFactor);
  const before = BigInt(last.unitPrice) * BigInt(price.unitFactor);
  return Number(divideRounded((now - before) * 10_000n, before, 'HALF_UP'));
}

export type UnitQuantityProblem = QuantityProblem | 'not_whole';

/**
 * A quantity as typed in a line's unit, in stock units. With no purchase unit (`unitFactor` null)
 * it is the display unit: pieces, or kg / L with up to 3 decimals. With a purchase unit (a box of
 * 24) it is a count of that unit, up to 3 decimals, as long as it makes whole stock units:
 * "10" boxes → 240, "1.5" → 36, "1.3" boxes of 24 → refused (31.2 pieces).
 */
export function parseQuantityInUnit(
  text: string,
  stockUnit: StockUnit,
  unitFactor: number | null,
):
  | { readonly ok: true; readonly value: number | null }
  | { readonly ok: false; readonly problem: UnitQuantityProblem } {
  if (unitFactor === null) {
    const parsed = parseQuantity(text, stockUnit);
    return parsed.ok ? parsed : { ok: false, problem: parsed.problem };
  }
  // Read as thousandths of the unit (the gram rule: three decimals at most).
  const milli = parseQuantity(text, 'G');
  if (!milli.ok) return { ok: false, problem: milli.problem };
  if (milli.value === null) return { ok: true, value: null };
  const stock = BigInt(milli.value) * BigInt(unitFactor);
  if (stock % 1000n !== 0n) return { ok: false, problem: 'not_whole' };
  const value = stock / 1000n;
  if (value > BigInt(SAFE)) return { ok: false, problem: 'too_large' };
  return { ok: true, value: Number(value) };
}

// ── What is sent to record an invoice ────────────────────────────────────────

export const PurchaseInvoiceLineSchema = z.strictObject({
  itemId: z.uuid(),
  /** One of the item's purchase units, or null: the price is per piece / kg / L. */
  unitId: z.uuid().nullable(),
  /** In the item's stock unit (pieces, grams, millilitres): never a fraction. */
  quantity: z.number().int().min(1).max(SAFE),
  /** Whole dinars per the line's unit. */
  unitPrice: z.number().int().min(0).max(SAFE),
  /** Required when the price is more than the setting above the last price. */
  priceJumpReason: z.string().trim().min(PRICE_JUMP_REASON_MIN).max(300).nullable().default(null),
});
export type PurchaseInvoiceLine = z.infer<typeof PurchaseInvoiceLineSchema>;

/**
 * Chosen by the device: a retry with the same id is the same invoice. A UUIDv7 or refused
 * (CLAUDE.md §3.3; owner, 2026-10-09).
 */
export const invoiceIdSchema = z
  .uuid()
  .refine(isUuidV7, { message: 'UUIDv7 expected', params: { dobCode: 'invalid_id' } });

/**
 * What an invoice says, wherever it is typed: online (the admin web), as a PC's sync command, or as
 * a PC's page hands it to its main process (W5-4). Only the place and the photos differ.
 */
export const invoiceContentFields = {
  supplierId: z.uuid(),
  number: z
    .string()
    .trim()
    .min(1)
    .max(INVOICE_NUMBER_MAX)
    .refine((n) => normalizeInvoiceNumber(n) !== '', { message: 'required' }),
  invoiceDate: z.iso.date(),
  paymentKind: z.enum(PAYMENT_KINDS),
  /** Null: the invoice date plus the supplier's terms (cash: the invoice date). */
  dueDate: z.iso.date().nullable().default(null),
  discount: z.number().int().min(0).max(SAFE).default(0),
  note: z.string().trim().max(500).nullable().default(null),
  /** A cancelled invoice of the same supplier and place this one is recorded in place of (Q2). */
  replacesId: z.uuid().nullable().default(null),
  lines: z.array(PurchaseInvoiceLineSchema).min(1).max(INVOICE_LINES_MAX),
};

/** A due date, when typed, is not before the invoice. */
export function dueNotBeforeInvoice(i: {
  readonly dueDate: string | null;
  readonly invoiceDate: string;
}): boolean {
  return i.dueDate === null || i.dueDate >= i.invoiceDate;
}
export const DUE_BEFORE_INVOICE: { message: string; path: string[] } = {
  message: 'due_before_invoice',
  path: ['dueDate'],
};

/**
 * "No photo, no invoice": its own photos, or the photos a phone sent through the QR code (W5-6; the
 * server links them and refuses the invoice when none arrived).
 */
export const PHOTO_REQUIRED: { message: string; path: string[]; params: { dobCode: string } } = {
  message: 'photo_required',
  path: ['attachmentIds'],
  params: { dobCode: 'photo_required' },
};

export const PurchaseInvoiceRecordSchema = z
  .strictObject({
    id: invoiceIdSchema,
    locationId: z.uuid(),
    ...invoiceContentFields,
    /** Stored INVOICE photos of this location. */
    attachmentIds: z.array(z.uuid()).max(INVOICE_PHOTOS_MAX).default([]),
    /** The photos a phone sent for this page (W5-6). */
    photoHandoff: PhotoHandoffRefSchema.nullable().default(null),
  })
  .refine(dueNotBeforeInvoice, DUE_BEFORE_INVOICE)
  .refine((i) => i.attachmentIds.length > 0 || i.photoHandoff !== null, PHOTO_REQUIRED);
export type PurchaseInvoiceRecord = z.infer<typeof PurchaseInvoiceRecordSchema>;
export type PurchaseInvoiceRecordInput = z.input<typeof PurchaseInvoiceRecordSchema>;

export const PurchaseInvoiceCancelSchema = z.strictObject({
  reason: z.string().trim().min(CANCEL_REASON_MIN).max(500),
});

/** The due date when none is typed: cash at once; credit after the supplier's terms. */
export function defaultDueDate(
  invoiceDate: string,
  paymentKind: PaymentKind,
  termsDays: number,
): string {
  if (paymentKind === 'CASH' || termsDays === 0) return invoiceDate;
  const day = Date.parse(`${invoiceDate}T00:00:00Z`);
  return new Date(day + termsDays * 86_400_000).toISOString().slice(0, 10);
}
